import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Mail, ShieldCheck, GraduationCap, Newspaper, LifeBuoy, Building2, Home, User, Scale, FileText } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";

const CONTENT = {
  "personal-credit": {
    eyebrow: "Products", icon: User, title: "Personal Credit",
    lead: "Understand and strengthen your personal credit with clear, bureau-level insight and an interactive plan to improve it.",
    sections: [
      { h: "See where you stand", t: "Track your score across bureaus, view the exact factors moving it, and watch your trajectory over time — no jargon." },
      { h: "Simulate before you act", t: "Our Score Simulator projects how paying down a balance or avoiding new inquiries could change your score, so you act with confidence." },
      { h: "A plan that fits you", t: "Get a personalized action plan — 'do these 3 things → +N points' — prioritized by impact." },
    ], cta: true,
  },
  "business-credit": {
    eyebrow: "Products", icon: Building2, title: "Business Credit",
    lead: "Build a fundable business credit profile lenders and vendors trust — across every major business bureau.",
    sections: [
      { h: "One connected file", t: "We normalize D&B, Experian and Equifax Commercial signals into a single, clear picture of your business creditworthiness." },
      { h: "Grow with tradelines", t: "See which reporting tradelines strengthen your file and how utilization and payment history drive your scores." },
      { h: "Get fundable", t: "Tie every improvement back to your MMT Funding Readiness Score and the capital it can unlock." },
    ], cta: true,
  },
  "real-estate": {
    eyebrow: "Products", icon: Home, title: "Real Estate",
    lead: "Turn credit strength into income-producing residential and commercial assets.",
    sections: [
      { h: "From credit to capital", t: "Leverage your improved credit and funding readiness toward property acquisition strategies." },
      { h: "Guided pathways", t: "Understand the financing options and readiness milestones that real-estate lenders look for." },
    ], soon: true, cta: true,
  },
  "news-insights": {
    eyebrow: "Resources", icon: Newspaper, title: "News & Insights",
    lead: "Timely analysis on credit, capital markets and the funding environment for business owners.",
    sections: [
      { h: "Powered by live research", t: "Signed-in members get current, cited answers on rates, lenders and programs through MMT's live web research." },
      { h: "Curated briefings", t: "Editorial breakdowns of what's changing in small-business finance and what it means for you." },
    ], soon: true,
  },
  "financial-education": {
    eyebrow: "Resources", icon: GraduationCap, title: "Financial Education",
    lead: "Plain-English guides that turn financial complexity into confident decisions.",
    sections: [
      { h: "Foundations", t: "Budgeting, cash flow, and the fundamentals of personal and business credit." },
      { h: "Build & leverage", t: "How to establish credit, manage utilization, and use capital responsibly to grow." },
    ], soon: true,
  },
  "business-credit-education": {
    eyebrow: "Resources", icon: GraduationCap, title: "Business Credit Education",
    lead: "A step-by-step curriculum for establishing and scaling business credit from day one.",
    sections: [
      { h: "Establish", t: "Entity setup, EIN, DUNS, and the identity foundation bureaus verify against." },
      { h: "Report & grow", t: "Vendor tradelines, revolving lines, and the reporting habits that build a strong file." },
    ], soon: true,
  },
  "help-center": {
    eyebrow: "Resources", icon: LifeBuoy, title: "Help Center",
    lead: "Answers, guides and support for getting the most out of MMT.",
    sections: [
      { h: "Ask MMT", t: "Signed-in members can press Cmd/Ctrl+K anytime to ask the AI concierge for help across the platform." },
      { h: "Need a human?", t: "Reach our team any time — see the Contact page for the fastest way to get support." },
    ],
  },
  "about": {
    eyebrow: "Company", icon: Building2, title: "About MMT",
    lead: "Millionaire Mindsetters Technologies Inc. builds next-generation financial technology so people and businesses can plan smarter and build bigger.",
    sections: [
      { h: "Our mission", t: "People. Plan. Opportunity. We turn credit and capital complexity into a clear, guided path to funding and wealth." },
      { h: "How we're different", t: "An intent-driven Adaptive OS, a connected Financial Graph, and a strict compliance-first approach — real intelligence, never hype." },
    ],
  },
  "privacy-policy": {
    eyebrow: "Company", icon: ShieldCheck, title: "Privacy Policy",
    lead: "Your data is used only for permissible purposes you authorize, and never sold.",
    sections: [
      { h: "What we collect", t: "Account details you provide and the financial data you explicitly connect. You control which sources are linked." },
      { h: "How we use it", t: "Solely to deliver the services you request — credit insight, funding readiness and matching — under documented permissible purpose." },
      { h: "Your controls", t: "You can review connected sources, revoke access, and request data actions from your account at any time." },
      { h: "Contact", t: "Questions about privacy? Reach us via the Contact page and we'll respond promptly." },
    ],
  },
  "terms-of-service": {
    eyebrow: "Company", icon: Scale, title: "Terms of Service",
    lead: "The terms governing your use of the MMT platform.",
    sections: [
      { h: "Use of the service", t: "MMT provides financial intelligence and readiness tools. It is not a lender, credit-repair organization, or a guarantee of approval." },
      { h: "Sandbox & demo data", t: "Where features rely on external providers not yet connected, data is clearly labeled SANDBOX/DEMO or COMING SOON." },
      { h: "Acceptable use", t: "You agree to provide accurate information and use the platform lawfully and for permissible purposes." },
    ],
  },
  "compliance-disclosures": {
    eyebrow: "Company", icon: FileText, title: "Compliance & Disclosures",
    lead: "Compliance is built into MMT, not bolted on.",
    sections: [
      { h: "Permissible purpose", t: "Every data access is tied to a documented permissible purpose, with a full audit trail in our Governance Center." },
      { h: "No fabricated outcomes", t: "Scores are indicators, not guarantees. We never present speculative approvals as real offers." },
      { h: "Provider readiness", t: "Bureau reporting and lending features display their true status — LIVE, SANDBOX, or PROVIDER APPROVAL PENDING." },
    ],
  },
  "contact": {
    eyebrow: "Company", icon: Mail, title: "Contact Us",
    lead: "We'd love to hear from you.",
    sections: [
      { h: "General & support", t: "Email support@smarterchoiceholdings.com and our team will get back to you quickly." },
      { h: "Inside the app", t: "Signed-in members can also press Cmd/Ctrl+K to ask MMT for instant help." },
    ],
  },
};

export default function InfoPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const page = CONTENT[slug];
  if (!page) return <Navigate to="/404" replace />;
  const Icon = page.icon || FileText;
  const primaryTo = user ? "/adaptive" : "/login";

  return (
    <div className="min-h-screen bg-white" data-testid="info-page">
      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <Link to="/" data-testid="info-logo"><Logo variant="dark" height={26} /></Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="rounded-xl border border-slate-300 px-4 py-2 text-[13px] font-semibold text-slate-800 hover:border-slate-400">Sign In</Link>
            <Link to={primaryTo} className="rounded-xl bg-[#0f172a] px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#1a5fd0]">Get Started</Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-14 lg:px-8">
        <Link to="/" data-testid="info-back" className="mb-8 inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-[#1a5fd0]"><ArrowLeft size={15} /> Back to home</Link>
        <div className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#b0862f]">{page.eyebrow}</div>
        <div className="mt-4 flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1a5fd0]/10"><Icon size={26} className="text-[#1a5fd0]" /></div>
          <h1 className="font-head text-4xl font-extrabold text-slate-900 sm:text-5xl">{page.title}</h1>
        </div>
        {page.soon && <span className="mt-4 inline-block rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-500">Coming soon</span>}
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">{page.lead}</p>

        <div className="mt-10 space-y-8">
          {page.sections.map((s) => (
            <div key={s.h} className="border-l-2 border-[#1a5fd0]/30 pl-5">
              <h2 className="font-head text-lg font-bold text-slate-900">{s.h}</h2>
              <p className="mt-1.5 text-[15px] leading-relaxed text-slate-600">{s.t}</p>
            </div>
          ))}
        </div>

        {page.cta && (
          <div className="mt-12 flex flex-col items-start gap-4 rounded-2xl bg-[#080d1a] p-8 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="font-head text-xl font-bold text-white">Ready to build?</div>
              <p className="mt-1 text-[14px] text-slate-300">Create your free account and start your plan in minutes.</p>
            </div>
            <Link to={primaryTo} data-testid="info-cta" className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-[#b0862f] to-[#d8b25a] px-6 py-3.5 text-sm font-semibold text-[#0f172a] active:scale-95">Get Started Free <ArrowRight size={16} /></Link>
          </div>
        )}
      </main>

      <footer className="border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-5 py-6 lg:px-8">
          <span className="font-mono text-[11px] text-slate-400">© 2026 Millionaire Mindsetters Technologies (MMT). All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
